<!-- eslint-disable vuejs-accessibility/form-control-has-label -->
<template>
  <select
    v-model="localValue"
    :class="selectClasses"
    @change="onChange"
    @blur="onBlur"
    @focus="onFocus"
  >
    <template
      v-for="option, index in formOptions"
      :key="index"
    >
      <slot />
      <BFormSelectOptionGroup
        v-if="Array.isArray(option)"
        label="Hello"
        :options="option"
      />
      <BFormSelectOption
        v-else
        :value="option.value"
        :disabled="option.disabled"
      >
        {{ option.text }}
      </BFormSelectOption>
    </template>
    <slot />
  </select>
</template>

<script>
import BFormSelectOption from './BFormSelectOption.vue'
import BFormSelectOptionGroup from './BFormSelectOptionGroup.vue'

export const isPlainObject = obj => Object.prototype.toString.call(obj) === '[object Object]'

export default {
  name: 'BFormSelect',
  components: {
    BFormSelectOption,
    BFormSelectOptionGroup
  },
  props: {
    multiple: Boolean,
    plain: Boolean,
    modelValue: {
      type: [String, Number, Array],
      default: null
    },
    value: {
      type: [String, Number],
      default: null
    },
    size: {
      type: String,
      default: 'md'
    },
    options: {
      type: Array,
      default: () => []
    },
    textField: {
      type: String,
      default: 'text'
    },
    valueField: {
      type: String,
      default: 'value'
    },
    disabledField: {
      type: String,
      default: 'disabled'
    }
  },
  emits: ['update:modelValue', 'change', 'input', 'blur', 'focus'],
  data() {
    return {
      localValue: this.modelValue || this.value
    }
  },
  computed: {
    formOptions() {
      return this.normalizeOptions(this.options)
    },
    selectClasses() {
      return [
        this.plain ? 'form-control' : 'custom-select',
        this.size && this.plain ? `form-control-${this.size}` : null,
        this.size && !this.plain ? `custom-select-${this.size}` : null
      ]
    }
  },
  watch: {
    modelValue(n, o) {
      if (n !== o) {
        this.localValue = n
      }
    },
    value(n, o) {
      if (n !== o) {
        this.localValue = n
      }
    }
  },
  methods: {
    formatValue(event) {
      const { target } = event
      const selectedValue = Array.from(target.options)
        .filter(o => o.selected)
        .map(o => ('_value' in o ? o._value : o.value))

      return target.multiple ? selectedValue : selectedValue[0]
    },
    normalizeOption(option, key = null) {
      // When the option is an object, normalize it
      if (isPlainObject(option)) {
        const value = option[this.valueField]
        const text = option[this.textField]
        return {
          value: value === undefined ? key || text : value,
          text: String(text === undefined ? key : text),
          disabled: Boolean(option[this.disabledField])
        }
      }
      // Otherwise create an `<option>` object from the given value
      return {
        value: key || option,
        text: String(option),
        disabled: false
      }
    },
    normalizeOptions(options) {
      // Normalize the given options array
      if (Array.isArray(options)) {
        return options.map(option => this.normalizeOption(option))
      } else if (isPlainObject(options)) {
        // Normalize a `options` object to an array of options
        return Object.keys(options).map(key => this.normalizeOption(options[key] || {}, key))
      }
      // If not an array or object, return an empty array
      return []
    },
    doModelUpdate(value) {
      this.$emit('update:modelValue', value)
    },
    onChange(e) {
      if (this.modelValue !== undefined) {
        this.doModelUpdate(this.formatValue(e))
        this.$emit('input', this.formatValue(e))
      }
      this.$emit('change', this.formatValue(e))
    },
    onBlur(e) {
      this.$emit('blur', e.target.value)
    },
    onFocus(e) {
      this.$emit('focus', e.target.value)
    },
    focus() {
      this.$el.focus()
    },
    blur() {
      this.$el.blur()
    }
  }
}
</script>
